import { FC, useEffect, useState } from "react";

import { Collapse, Drawer, Message, Messages, Spinner } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { formularyDrugDetailsAPI } from "@/api";
import type { RawFormularyRecord } from "./DrugCoverage";
import styles from "./DrugDetailsDrawer.module.scss";

type DrugDetailsSectionKey =
  | "common_uses"
  | "how_to_use"
  | "possible_side_effects"
  | "precautions"
  | "drug_interactions"
  | "overdose"
  | "additional_information"
  | "missed_dose"
  | "storage"
  | "warning";

type DrugDetailsBlockType =
  | "heading"
  | "paragraph"
  | "bullet_list"
  | "numbered_list";

interface DrugDetailsListItem {
  id: string;
  text: string;
}

interface DrugDetailsTextBlock {
  id: string;
  type: Extract<DrugDetailsBlockType, "heading" | "paragraph">;
  text: string;
}

interface DrugDetailsListBlock {
  id: string;
  type: Extract<DrugDetailsBlockType, "bullet_list" | "numbered_list">;
  items: DrugDetailsListItem[];
}

type DrugDetailsBlock = DrugDetailsTextBlock | DrugDetailsListBlock;

interface DrugDetailsSection {
  key: DrugDetailsSectionKey;
  title: string;
  blocks: DrugDetailsBlock[];
}

interface DrugDetails {
  id: number;
  ndc: string;
  generic_name: string | null;
  common_brand_names: string[];
  manufacturer_name: string | null;
  product_type: string | null;
  sections: DrugDetailsSection[];
}

interface DrugDetailsDrawerProps {
  selectedDrug: RawFormularyRecord;
  onClose: () => void;
}

export const DrugDetailsDrawer: FC<DrugDetailsDrawerProps> = ({
  selectedDrug,
  onClose,
}) => {
  const [drugDetails, setDrugDetails] = useState<DrugDetails | null>(null);
  const [expandedSectionKeys, setExpandedSectionKeys] = useState<DrugDetailsSectionKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;

    const loadDrugDetails = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setDrugDetails(null);
        setExpandedSectionKeys([]);

        const response = await formularyDrugDetailsAPI({
          ndc: selectedDrug.ndc,
          structured_sections: true,
        });

        if (!isActive) {
          return;
        }

        if (!response?.fda_drug_label) {
          throw new Error("fda_drug_label missing from drug details response");
        }

        setDrugDetails(response.fda_drug_label);
      } catch (err) {
        if (!isActive) {
          return;
        }

        console.error("Failed to load drug details:", err);
        setErrorMessage("Unable to load prescribing information.");
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadDrugDetails();

    return () => {
      isActive = false;
    };
  }, [selectedDrug.ndc]);

  const availableSections = drugDetails?.sections ?? [];

  const handleSectionToggle = (
    sectionKey: DrugDetailsSectionKey,
    isExpanded: boolean,
  ) => {
    setExpandedSectionKeys((currentSectionKeys) => {
      if (isExpanded) {
        return [...currentSectionKeys, sectionKey];
      }

      return currentSectionKeys.filter((key) => key !== sectionKey);
    });
  };

  return (
    <Drawer
      className={styles.Drawer}
      id="drug-details-drawer"
      visible
      title={selectedDrug.label_name}
      onClose={onClose}
    >
      {isLoading && (
        <Stack alignment="center" className={styles.Loading}>
          <Spinner size="large" />
        </Stack>
      )}

      {!!errorMessage && (
        <Message appearance={Messages.Error} visible>
          {errorMessage}
        </Message>
      )}

      {!!drugDetails && (
        <Stack spacing="medium" className={styles.Content}>
          <Stack spacing="mini">
            <Semibold className={styles.MetadataTitle}>Generic Name</Semibold>
            <Body className={styles.Description}>
              {drugDetails.generic_name || "--"}
            </Body>
          </Stack>

          <Stack spacing="mini">
            <Semibold className={styles.MetadataTitle}>Common Brand Name(s)</Semibold>
            <Body className={styles.Description}>
              {drugDetails.common_brand_names.join(", ") || "--"}
            </Body>
          </Stack>

          {!!availableSections.length && (
            <div className={styles.Sections}>
              {availableSections.map(({ key, title, blocks }) => (
                <Collapse
                  alignCaret="left"
                  appearance="blank"
                  className={styles.Section}
                  expanded={expandedSectionKeys.includes(key)}
                  key={key}
                  onToggleExpand={(isExpanded) => handleSectionToggle(key, isExpanded)}
                  testId={`drug-details-section-${key}`}
                  title={title}
                >
                  <div className={styles.Description}>
                    {blocks.map(renderDescriptionBlock)}
                  </div>
                </Collapse>
              ))}
            </div>
          )}
        </Stack>
      )}
    </Drawer>
  );
};

const renderDescriptionBlock = (block: DrugDetailsBlock) => {
  if (block.type === "heading") {
    return <h4 key={block.id}>{block.text}</h4>;
  }

  if (block.type === "bullet_list") {
    return (
      <ul key={block.id}>
        {block.items.map((item) => (
          <li key={item.id}>{item.text}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "numbered_list") {
    return (
      <ol key={block.id}>
        {block.items.map((item) => (
          <li key={item.id}>{item.text}</li>
        ))}
      </ol>
    );
  }

  if (block.type === "paragraph") {
    return <p key={block.id}>{block.text}</p>;
  }

  return null;
};
